import React, { useMemo } from "react";
import {
  DURATION_PRESETS,
  FORMAT_OPTIONS,
  LANG_OPTIONS,
  PROVIDER_OPTIONS,
  RECURRENCE_OPTIONS,
  VALIDATION,
  WEBINAR_TIMEZONE,
} from "../../../constants";
import {
  earliestAllowedStart,
  isSessionDateNotPast,
  isSessionStartAllowed,
  minTimeForSessionDate,
  todayYmdInTimezone,
} from "../../../utils/deriveStartAt";
import { isJoinUrlOk, isRecEndOk } from "../../../utils/sessionValidation";
import { SessionFormState } from "../../../utils/sessionFormState";

type Props = {
  form: SessionFormState;
  setField: <K extends keyof SessionFormState>(
    key: K,
    value: SessionFormState[K],
  ) => void;
  hostClash: boolean;
};

const FORMAT_NOTES: Record<string, string> = {
  "Talk + Q&A": "Default 45 min · single-speaker live console.",
  Workshop: "Default 60 min · slides + breakouts.",
  "Group consult": "Default 30 min · capped, camera-on layout.",
  AMA: "Default 40 min · moderated member questions.",
};

const ScheduleStep: React.FC<Props> = ({ form, setField, hostClash }) => {
  const recOff = form.recurrence === "none";
  const minDate = useMemo(() => {
    const earliest = earliestAllowedStart();
    const today = todayYmdInTimezone(WEBINAR_TIMEZONE);
    return earliest.date > today ? earliest.date : today;
  }, []);

  const minTimeHint = useMemo(
    () => minTimeForSessionDate(form.date || minDate),
    [form.date, minDate],
  );

  const dateOk = !form.date || isSessionDateNotPast(form.date);
  const timeOk =
    !form.date || !form.time || isSessionStartAllowed(form.date, form.time);
  const whenOk = dateOk && timeOk;
  const recEndOk = isRecEndOk(form);
  const urlOk =
    !form.joinUrl.trim() || isJoinUrlOk(form.provider, form.joinUrl);
  const recEndMin = form.date && form.date >= minDate ? form.date : minDate;

  const onDateChange = (value: string) => {
    let nextDate = value;
    if (nextDate && nextDate < minDate) nextDate = minDate;
    setField("date", nextDate);
    // Keep the selected time even if it becomes invalid — validation shows the error.
  };

  const onTimeChange = (value: string) => {
    // Allow any selection; publish/draft validation + UI error handle the rest.
    setField("time", value);
  };

  const onRecEndChange = (value: string) => {
    if (value && value < recEndMin) {
      setField("recEnd", recEndMin);
      return;
    }
    setField("recEnd", value);
  };

  const whenHint = (() => {
    if (whenOk) {
      return `Start at least ${VALIDATION.startMinHours} hours from now (${WEBINAR_TIMEZONE}). Past date/time cannot be selected.`;
    }
    if (!dateOk) {
      return "Past dates cannot be selected. Pick today or a future date.";
    }
    if (minTimeHint) {
      return `Start time must be at least ${VALIDATION.startMinHours} hours from now. Earliest allowed for this date: ${minTimeHint}.`;
    }
    return `Start time must be at least ${VALIDATION.startMinHours} hours from now.`;
  })();

  return (
    <div className="wf-step">
      <div>
        <div className="wf-grid-2">
          <div className="wf-field">
            <span>
              Date <span className="req">*</span>
            </span>
            <input
              type="date"
              value={form.date}
              min={minDate}
              aria-invalid={!dateOk}
              onChange={(e) => onDateChange(e.target.value)}
              style={{
                borderColor: dateOk ? undefined : "#E8B3A8",
              }}
            />
          </div>
          <div className="wf-field">
            <span>
              Start time <span className="req">*</span>
            </span>
            <input
              type="time"
              value={form.time}
              step={300}
              aria-invalid={!timeOk}
              onChange={(e) => onTimeChange(e.target.value)}
              style={{
                borderColor: timeOk ? undefined : "#E8B3A8",
              }}
            />
          </div>
        </div>
        <div
          className="wf-field__hint"
          style={{
            marginTop: 6,
            color: whenOk ? "#918A82" : "#B4483A",
            fontSize: 14,
          }}
          role={whenOk ? undefined : "alert"}
        >
          {whenHint}
        </div>
      </div>

      {hostClash ? (
        <div className="wf-warn-box">
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#B4483A"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M12 3l9.5 17H2.5z" />
            <path d="M12 9.5v4.5M12 16.8v.2" />
          </svg>
          <p>
            This host already has a scheduled session within 90 minutes of this
            slot.
          </p>
        </div>
      ) : null}

      <div>
        <label className="wf-field__label" style={{ marginBottom: 9 }}>
          Duration <span className="req">*</span>
        </label>
        <div className="wf-duration">
          {DURATION_PRESETS.map((m) => {
            const active = Number(form.mins) === m;
            return (
              <button
                key={m}
                type="button"
                className={`wf-duration__pill${active ? " is-active" : ""}`}
                onClick={() => setField("mins", m)}
              >
                {m} min
              </button>
            );
          })}
          <div className="wf-duration__custom">
            <input
              type="number"
              min={VALIDATION.minsMin}
              max={VALIDATION.minsMax}
              step={VALIDATION.minsStep}
              value={form.mins}
              onChange={(e) => setField("mins", Number(e.target.value))}
            />
            <span>
              min · {VALIDATION.minsMin}–{VALIDATION.minsMax}, steps of{" "}
              {VALIDATION.minsStep}
            </span>
          </div>
        </div>
      </div>

      <div>
        <div className="wf-grid-2">
          <div className="wf-field">
            <span>Recurrence</span>
            <select
              value={form.recurrence}
              onChange={(e) => setField("recurrence", e.target.value)}
            >
              {RECURRENCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="wf-field">
            <span>Series ends</span>
            <input
              type="date"
              value={form.recEnd}
              min={recEndMin}
              disabled={recOff}
              aria-invalid={!recEndOk}
              onChange={(e) => onRecEndChange(e.target.value)}
              style={{
                borderColor: recEndOk ? undefined : "#E8B3A8",
              }}
            />
          </div>
        </div>
        <div
          className="wf-field__hint"
          style={{
            marginTop: 6,
            color: recEndOk ? undefined : "#B4483A",
          }}
        >
          {recOff
            ? "A one-off session."
            : recEndOk
              ? "Generates a series. Each occurrence stays independently editable and cancellable."
              : "Series end must be on or after the session date."}
        </div>
      </div>

      <div className="wf-grid-2">
        <div className="wf-field">
          <span>
            Format <span className="req">*</span>
          </span>
          <select
            value={form.format}
            onChange={(e) => setField("format", e.target.value)}
          >
            {FORMAT_OPTIONS.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
          <div className="wf-field__hint">
            {FORMAT_NOTES[form.format] || ""}
          </div>
        </div>
        <div className="wf-field">
          <span>
            Language <span className="req">*</span>
          </span>
          <select
            value={form.lang}
            onChange={(e) => setField("lang", e.target.value)}
          >
            {LANG_OPTIONS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
          <div className="wf-field__hint">
            Shows on the session as “{form.lang}”.
          </div>
        </div>
      </div>

      <div className="wf-streaming">
        <div className="wf-streaming__title">
          Streaming <span className="req">*</span>
        </div>
        <div className="wf-streaming__row">
          <select
            value={form.provider}
            onChange={(e) => setField("provider", e.target.value)}
          >
            {PROVIDER_OPTIONS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={form.joinUrl}
            onChange={(e) => setField("joinUrl", e.target.value)}
            placeholder="Join URL or room id"
            aria-invalid={!urlOk}
            style={{
              borderColor: urlOk ? undefined : "#E8B3A8",
            }}
          />
        </div>
        <div
          className="wf-streaming__lock"
          style={{ color: urlOk ? undefined : "#B4483A" }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <rect x="4" y="10.5" width="16" height="11" rx="3" />
            <path d="M8 10.5V7.5a4 4 0 018 0v3" />
          </svg>
          <span>
            {urlOk
              ? "Use https://… or a room id. Never exposed to members before T‑15 min."
              : "Enter a valid https URL or room id (min 4 characters)."}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ScheduleStep;
